import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useLanguage } from '@/hooks/useLanguage';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form } from '@/components/ui/form';
import { Progress } from '@/components/ui/progress';
import { useToast } from '@/hooks/use-toast';
import { useBookingSubmission } from '@/hooks/useBookingSubmission';
import { 
  Briefcase, Scale, HeartPulse, Users, Calculator, FileText, Home, Building2 
} from 'lucide-react';

import ServiceSelectionStep from '@/components/booking/ServiceSelectionStep';
import BasicInfoStep from '@/components/booking/BasicInfoStep';
import AppointmentStep from '@/components/booking/AppointmentStep';
import ReferralStep from '@/components/booking/ReferralStep';
import PaymentStep from '@/components/booking/PaymentStep';
import ConsentStep from '@/components/booking/ConsentStep';

const services = [
  { id: 'Immigration', icon: Briefcase },
  { id: 'Criminal Defense', icon: Scale },
  { id: 'Personal Injury', icon: HeartPulse },
  { id: 'Family Law', icon: Users },
  { id: 'Tax', icon: Calculator },
  { id: 'Probate & Estate Planning', icon: FileText },
  { id: 'Real Estate', icon: Home },
  { id: 'Business Law', icon: Building2 },
];

const createFormSchema = (t: any) => z.object({
  service: z.string().min(1, t('booking.required')),
  fullName: z.string()
    .min(1, t('booking.required'))
    .max(100, "Name must be less than 100 characters.")
    .regex(/^[a-zA-ZÀ-ÿ\s\-']+$/, "Name contains invalid characters."),
  dateOfBirth: z.string().min(1, t('booking.required')),
  email: z.string()
    .email(t('booking.invalidEmail'))
    .max(255, "Email must be less than 255 characters.")
    .toLowerCase()
    .refine((email) => {
      const emailRegex = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
      return emailRegex.test(email);
    }, t('booking.invalidEmail')),
  phone: z.string()
    .min(10, t('booking.invalidPhone'))
    .max(20, "Phone number is too long.")
    .regex(/^[\d\s\-\(\)\+]+$/, "Phone number contains invalid characters."),
  address: z.string().max(500, "Address is too long.").optional(),
  employerTitle: z.string().max(200, "Title is too long.").optional(),
  situation: z.string()
    .min(30, t('booking.minLength').replace('{count}', '30'))
    .max(5000, "Description is too long.")
    .refine((msg) => {
      const sqlPattern = /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|CREATE|ALTER|EXEC|EXECUTE|UNION|DECLARE)\b)|(-{2})/gi;
      return !sqlPattern.test(msg);
    }, "Description contains invalid content."),
  immigrationData: z.any().optional(),
  criminalData: z.any().optional(),
  injuryData: z.any().optional(),
  familyData: z.any().optional(),
  taxData: z.any().optional(),
  probateData: z.any().optional(),
  realEstateData: z.any().optional(),
  businessData: z.any().optional(),
  appointmentType: z.enum(['In-person', 'Phone', 'Video']),
  preferredDate: z.string().min(1, t('booking.required')),
  preferredTime: z.string().min(1, t('booking.required')),
  referredBy: z.string().min(1, t('booking.required')),
  referredOther: z.string().optional(),
  paymentMethod: z.enum(['Stripe', 'Check', 'Wire']),
  paymentEvidenceFile: z.any().optional(),
  consentNotifications: z.boolean().refine(val => val === true, t('booking.required')),
  consentTerms: z.boolean().refine(val => val === true, t('booking.required')),
  marketingConsent: z.boolean().optional(),
});

interface BookingFormProps {
  preselectedService?: string | null;
}

const BookingForm = ({ preselectedService }: BookingFormProps) => {
  const { t, language } = useLanguage();
  const { toast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  
  const formTopRef = useRef<HTMLDivElement>(null);
  const nextButtonRef = useRef<HTMLButtonElement>(null);

  const totalSteps = 6;

  const form = useForm<z.infer<ReturnType<typeof createFormSchema>>>({
    resolver: zodResolver(createFormSchema(t)),
    defaultValues: {
      service: preselectedService || '',
      fullName: '',
      dateOfBirth: '',
      email: '',
      phone: '',
      address: '',
      employerTitle: '',
      situation: '',
      appointmentType: 'In-person',
      referredBy: '',
      referredOther: '',
      paymentMethod: 'Stripe',
      consentNotifications: false,
      consentTerms: false,
      marketingConsent: false,
    },
  });

  const { isSubmitting, onSubmit } = useBookingSubmission({ form, setCurrentStep });

  const scrollToNextButton = () => {
    setTimeout(() => {
      nextButtonRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  // Save to localStorage on change
  useEffect(() => {
    const subscription = form.watch((value) => {
      const { paymentEvidenceFile, ...serializableData } = value;
      localStorage.setItem('bookingFormData', JSON.stringify(serializableData));
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('bookingFormData');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        Object.keys(data).forEach((key) => {
          form.setValue(key as any, data[key]);
        });
      } catch (e) {
        console.error('Failed to load saved form data', e);
      }
    }
  }, []);

  // Set preselected service if provided
  useEffect(() => {
    if (preselectedService) {
      form.setValue('service', preselectedService);
      if (currentStep === 1) setCurrentStep(2);
    }
  }, [preselectedService]);

  const nextStep = async () => {
    let fieldsToValidate: any[] = [];
    
    switch (currentStep) {
      case 1: fieldsToValidate = ['service']; break;
      case 2: fieldsToValidate = ['fullName', 'dateOfBirth', 'email', 'phone', 'situation']; break;
      case 3: fieldsToValidate = ['appointmentType', 'preferredDate', 'preferredTime']; break;
      case 4: fieldsToValidate = ['referredBy']; break;
      case 5:
        fieldsToValidate = ['paymentMethod'];
        break;
      case 6: fieldsToValidate = ['consentNotifications', 'consentTerms']; break;
    }

    const isValid = await form.trigger(fieldsToValidate as any);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, totalSteps));
      setTimeout(() => {
        formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
    setTimeout(() => {
      formTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  };

  const progress = (currentStep / totalSteps) * 100;

  return (
    <div ref={formTopRef}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-2xl">
            {t('booking.step')} {currentStep} {t('booking.of')} {totalSteps}
          </CardTitle>
          <CardDescription>
            <Progress value={progress} className="mt-2" />
          </CardDescription>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              {currentStep === 1 && <ServiceSelectionStep form={form} services={services} onSelect={scrollToNextButton} />}
              {currentStep === 2 && <BasicInfoStep form={form} />}
              {currentStep === 3 && <AppointmentStep form={form} onSelect={scrollToNextButton} />}
              {currentStep === 4 && <ReferralStep form={form} onSelect={scrollToNextButton} />}
              {currentStep === 5 && <PaymentStep form={form} onSelect={scrollToNextButton} />}
              {currentStep === 6 && <ConsentStep form={form} onSelect={scrollToNextButton} />}

              {currentStep === 5 && (
                <div className="bg-destructive/20 border-2 border-destructive rounded-lg p-4 mt-2" role="alert" aria-live="polite">
                  <p className="font-bold text-destructive text-base mb-1">
                    {language === 'es' ? '⚠️ Política de pago' : '⚠️ Payment Policy'}
                  </p>
                  <p className="text-destructive text-sm">
                    {language === 'es'
                      ? 'La tarifa de consulta de $150 no es reembolsable. Este pago asegura su cita.'
                      : 'The $150 consultation fee is non-refundable. This payment secures your appointment.'}
                  </p>
                </div>
              )}

              <div className="flex justify-between pt-6 gap-4">
                {currentStep > 1 && (
                  <Button type="button" variant="outline" onClick={prevStep} size="lg" className="min-h-[48px] px-6">
                    {t('booking.previous')}
                  </Button>
                )}

                {currentStep < totalSteps ? (
                  <Button
                    ref={nextButtonRef}
                    type="button"
                    onClick={nextStep}
                    className="ml-auto min-h-[48px] px-8 text-base"
                    size="lg"
                  >
                    {t('booking.next')}
                  </Button>
                ) : (
                  <Button type="submit" disabled={isSubmitting} className="ml-auto min-h-[48px] px-8 text-base" size="lg">
                    {isSubmitting
                      ? (language === 'es' ? 'Enviando...' : 'Submitting...')
                      : t('booking.submit')
                    }
                  </Button>
                )}
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
};

export default BookingForm;
